import { useEffect, useState } from 'react';
import {
  Box,
  Chip,
  CircularProgress,
  MenuItem,
  Table,
  TableBody,
  TableFooter,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip,
} from '@mui/material';
import { api } from './api';
import { amount, date, Empty, money, unitPrice } from './ui';

export type StatsSide = 'SALE' | 'PURCHASE';

export interface PartnerStat {
  key: string;
  name: string;
  /** False for a counterparty recorded by this enterprise that is not on the platform. */
  platform: boolean;
  orders: number;
  items: number;
  quantity: string;
  amount: string;
  last_at: string;
}

export type Period = 'MONTH' | 'QUARTER' | 'YEAR' | 'ALL';
const periods: [Period, string][] = [
  ['MONTH', '本月'],
  ['QUARTER', '近三个月'],
  ['YEAR', '今年'],
  ['ALL', '全部时间'],
];

function periodFrom(period: Period) {
  const now = new Date();
  const start =
    period === 'MONTH'
      ? new Date(now.getFullYear(), now.getMonth(), 1)
      : period === 'QUARTER'
        ? new Date(now.getFullYear(), now.getMonth() - 2, 1)
        : period === 'YEAR'
          ? new Date(now.getFullYear(), 0, 1)
          : null;
  if (!start) return undefined;
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${start.getFullYear()}-${pad(start.getMonth() + 1)}-${pad(start.getDate())}`;
}

export function PeriodSelect(p: { value: Period; onChange: (period: Period) => void }) {
  return (
    <TextField
      select
      size="small"
      value={p.value}
      onChange={(e) => p.onChange(e.target.value as Period)}
      sx={{ width: 130 }}
    >
      {periods.map(([value, label]) => (
        <MenuItem key={value} value={value}>
          {label}
        </MenuItem>
      ))}
    </TextField>
  );
}

/** Loads per-partner totals; `null` while loading. A disabled side yields no rows, unfetched. */
export function usePartnerStats(
  side: StatsSide,
  sku: string | undefined,
  period: Period,
  enabled = true,
) {
  const [rows, setRows] = useState<PartnerStat[] | null>(null);
  const [error, setError] = useState('');
  useEffect(() => {
    let live = true;
    setError('');
    if (!enabled) {
      setRows([]);
      return;
    }
    setRows(null);
    const query = new URLSearchParams({ side });
    if (sku) query.set('sku', sku);
    const from = periodFrom(period);
    if (from) query.set('from', from);
    api<PartnerStat[]>(`/stats/partners?${query}`)
      .then((r) => live && setRows(r))
      .catch((e) => live && setError((e as Error).message));
    return () => {
      live = false;
    };
  }, [side, sku, period, enabled]);
  return { rows, error };
}

type SortKey = 'amount' | 'quantity' | 'price' | 'orders';
const priceOf = (r: PartnerStat) =>
  Number(r.quantity) > 0 ? Number(r.amount) / Number(r.quantity) : 0;
const sortValue = (r: PartnerStat, key: SortKey) =>
  key === 'price' ? priceOf(r) : key === 'orders' ? r.orders : Number(r[key]);

const totalsOf = (rows: PartnerStat[]) => ({
  orders: rows.reduce((s, r) => s + r.orders, 0),
  quantity: rows.reduce((s, r) => s + Number(r.quantity), 0),
  amount: rows.reduce((s, r) => s + Number(r.amount), 0),
});
const shareText = (share: number) =>
  share >= 0.995 ? '100%' : share < 0.01 ? '<1%' : `${Math.round(share * 100)}%`;
const words = (side: StatsSide) =>
  side === 'SALE' ? { verb: '销售', partner: '客户' } : { verb: '采购', partner: '供应商' };

/** Loading, error and empty states shared by the table and the list; null when there is data. */
function placeholder(side: StatsSide, rows: PartnerStat[] | null, error?: string) {
  if (error) return <Empty>{error}</Empty>;
  if (!rows)
    return (
      <Box sx={{ p: 6, textAlign: 'center' }}>
        <CircularProgress size={24} />
      </Box>
    );
  if (!rows.length) return <Empty>这段时间没有{words(side).verb}记录</Empty>;
  return null;
}

/**
 * The totals in one outlined box: the amount alone on top as the focal point, the supporting
 * figures in equal cells beneath it.
 */
export function StatsSummary(p: { side: StatsSide; rows: PartnerStat[]; unit?: string }) {
  const { verb, partner } = words(p.side);
  const total = totalsOf(p.rows);
  const only = p.rows.length === 1 ? p.rows[0] : null;
  const facts: [string, string][] = p.unit
    ? [
        [`${verb}数量（${p.unit}）`, amount(total.quantity)],
        ['平均单价', unitPrice(total.quantity ? total.amount / total.quantity : 0)],
        // With one partner its name is shown below, so the count gives way to the last date.
        only ? [`最近${verb}`, date(only.last_at)] : [partner, `${p.rows.length} 家`],
      ]
    : [
        ['订单', `${total.orders} 单`],
        [partner, `${p.rows.length} 家`],
      ];
  return (
    <Box
      sx={{
        border: 1,
        borderColor: 'divider',
        borderRadius: 1,
        overflow: 'hidden',
        fontVariantNumeric: 'tabular-nums',
      }}
    >
      <Box sx={{ px: 2.5, pt: 2, pb: 1.75 }}>
        <Box sx={{ color: 'text.secondary', fontSize: 13 }}>{verb}金额</Box>
        <Box
          sx={{ mt: 0.5, fontSize: 30, fontWeight: 700, letterSpacing: '-0.5px', lineHeight: 1.25 }}
        >
          {money(total.amount)}
        </Box>
      </Box>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: `repeat(${facts.length}, minmax(0, 1fr))`,
          borderTop: 1,
          borderColor: 'divider',
        }}
      >
        {facts.map(([label, value], i) => (
          <Box
            key={label}
            sx={{
              px: 2.5,
              py: 1.25,
              borderLeft: i ? 1 : 0,
              borderColor: 'divider',
              whiteSpace: 'nowrap',
            }}
          >
            <Box sx={{ color: 'text.secondary', fontSize: 12 }}>{label}</Box>
            <Box sx={{ mt: 0.25, fontSize: 14, fontWeight: 500 }}>{value}</Box>
          </Box>
        ))}
      </Box>
    </Box>
  );
}

/** A partner's name; one not on the platform carries an outlined 未入驻 tag after it. */
function PartnerName(p: { row: PartnerStat }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
      <Box
        sx={{
          minWidth: 0,
          color: 'text.primary',
          fontWeight: 600,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
        title={p.row.name}
      >
        {p.row.name}
      </Box>
      {!p.row.platform && (
        <Tooltip title="未使用联仓，由本企业登记单据、对账和收付款">
          <Chip
            size="small"
            variant="outlined"
            label="未入驻"
            sx={{ flex: 'none', height: 20, fontSize: 11, color: 'text.secondary' }}
          />
        </Tooltip>
      )}
    </Box>
  );
}

/**
 * One item's partners for the narrow drawer: the totals, then the compact table. A single
 * partner needs no ranking, so it is named in one line instead.
 */
export function ItemPartnerStats(p: {
  side: StatsSide;
  rows: PartnerStat[] | null;
  error?: string;
  unit: string;
}) {
  const empty = placeholder(p.side, p.rows, p.error);
  if (empty || !p.rows) return empty;
  const only = p.rows.length === 1 ? p.rows[0] : null;
  return (
    <Box sx={{ pb: 3 }}>
      <Box sx={{ px: 3, pb: 2 }}>
        <StatsSummary side={p.side} rows={p.rows} unit={p.unit} />
      </Box>
      {only ? (
        <Box
          sx={{
            px: 3,
            display: 'flex',
            alignItems: 'center',
            gap: 0.75,
            color: 'text.secondary',
            fontSize: 14,
          }}
        >
          {p.side === 'SALE' ? '全部销往' : '全部采购自'}
          <PartnerName row={only} />· {only.orders} 单
        </Box>
      ) : (
        <PartnerStatsTable side={p.side} rows={p.rows} unit={p.unit} compact />
      )}
    </Box>
  );
}

/**
 * Ranks customers (sales) or suppliers (purchases). For a single item, quantity and average price
 * are shown in that item's unit and a totals row closes the table; across items they would mix
 * units, so the table shows how many different items each partner traded instead. Amount and its
 * share of the total are separate right-aligned columns, so both scan as plain figures.
 */
export function PartnerStatsTable(p: {
  side: StatsSide;
  rows: PartnerStat[] | null;
  error?: string;
  /** Set when the rows are for one item; quantities are in this unit. */
  unit?: string;
  /** Four columns for the item drawer instead of the full-width page table. */
  compact?: boolean;
}) {
  const [sort, setSort] = useState<{ key: SortKey; desc: boolean }>({ key: 'amount', desc: true });
  const empty = placeholder(p.side, p.rows, p.error);
  if (empty || !p.rows) return empty;
  const { partner } = words(p.side);

  const perItem = !!p.unit;
  const rows = [...p.rows].sort((x, y) => {
    const d = sortValue(x, sort.key) - sortValue(y, sort.key);
    return sort.desc ? -d : d;
  });
  const total = totalsOf(rows);
  const head = (key: SortKey, label: string) => (
    <TableSortLabel
      active={sort.key === key}
      direction={sort.key === key && !sort.desc ? 'asc' : 'desc'}
      onClick={() => setSort((s) => ({ key, desc: s.key === key ? !s.desc : key !== 'price' }))}
    >
      {label}
    </TableSortLabel>
  );

  // The drawer's narrow form: orders and last date fold under the name, the share under the
  // amount; the totals are shown above it, so there is no footer.
  if (p.compact && perItem)
    return (
      <Table
        size="small"
        sx={{
          tableLayout: 'fixed',
          '& th, & td': { whiteSpace: 'nowrap', px: 2, verticalAlign: 'top' },
          '& th:first-of-type, & td:first-of-type': { pl: 3 },
          '& th:last-of-type, & td:last-of-type': { pr: 3 },
          '& tbody td': { py: 1.25, fontVariantNumeric: 'tabular-nums' },
        }}
      >
        <TableHead>
          <TableRow>
            <TableCell>{partner}</TableCell>
            <TableCell align="right" sx={{ width: 132 }}>
              {head('quantity', `数量（${p.unit}）`)}
            </TableCell>
            <TableCell align="right" sx={{ width: 96 }}>
              {head('price', '均价')}
            </TableCell>
            <TableCell align="right" sx={{ width: 140 }}>
              {head('amount', '金额')}
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((r) => (
            <TableRow key={r.key} hover>
              <TableCell>
                <PartnerName row={r} />
                <Box sx={{ mt: 0.25, color: 'text.secondary', fontSize: 12 }}>
                  {r.orders} 单 · 最近 {date(r.last_at)}
                </Box>
              </TableCell>
              <TableCell align="right">{amount(r.quantity)}</TableCell>
              <TableCell align="right">{unitPrice(priceOf(r))}</TableCell>
              <TableCell align="right">
                <Box sx={{ fontWeight: 650 }}>{money(r.amount)}</Box>
                <Box sx={{ mt: 0.25, color: 'text.secondary', fontSize: 12 }}>
                  {shareText(total.amount ? Number(r.amount) / total.amount : 0)}
                </Box>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    );

  return (
    <TableContainer>
      <Table
        size="small"
        sx={{
          tableLayout: 'fixed',
          minWidth: 760,
          // Figures and headers stay on one line; only the partner name may truncate.
          '& th, & td': { whiteSpace: 'nowrap', px: 2 },
          '& tbody td, & tfoot td': { py: 1.5, fontVariantNumeric: 'tabular-nums' },
          '& tfoot td': {
            color: 'text.primary',
            fontSize: 14,
            fontWeight: 600,
            bgcolor: '#f8fafb',
          },
        }}
      >
        <TableHead>
          <TableRow>
            <TableCell sx={{ width: 64, pl: '24px !important' }}>排名</TableCell>
            <TableCell>{partner}</TableCell>
            <TableCell align="right" sx={{ width: 96 }}>
              {head('orders', '订单数')}
            </TableCell>
            {perItem ? (
              <>
                <TableCell align="right" sx={{ width: 130 }}>
                  {head('quantity', `数量（${p.unit}）`)}
                </TableCell>
                <TableCell align="right" sx={{ width: 100 }}>
                  {head('price', '均价')}
                </TableCell>
              </>
            ) : (
              <TableCell align="right" sx={{ width: 100 }}>
                商品种数
              </TableCell>
            )}
            <TableCell align="right" sx={{ width: 170 }}>
              {head('amount', '成交金额')}
            </TableCell>
            <TableCell align="right" sx={{ width: 130 }}>
              占{partner}总额
            </TableCell>
            <TableCell align="right" sx={{ width: 120, pr: '24px !important' }}>
              最近成交
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((r, i) => (
            <TableRow key={r.key} hover>
              <TableCell sx={{ pl: '24px !important', color: 'text.secondary' }}>{i + 1}</TableCell>
              <TableCell>
                <PartnerName row={r} />
              </TableCell>
              <TableCell align="right">{r.orders}</TableCell>
              {perItem ? (
                <>
                  <TableCell align="right">{amount(r.quantity)}</TableCell>
                  <TableCell align="right">{unitPrice(priceOf(r))}</TableCell>
                </>
              ) : (
                <TableCell align="right">{r.items}</TableCell>
              )}
              <TableCell align="right" sx={{ fontWeight: 650 }}>
                {money(r.amount)}
              </TableCell>
              <TableCell align="right" sx={{ color: 'text.secondary' }}>
                {shareText(total.amount ? Number(r.amount) / total.amount : 0)}
              </TableCell>
              <TableCell align="right" sx={{ color: 'text.secondary', pr: '24px !important' }}>
                {date(r.last_at)}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
        {perItem && (
          <TableFooter>
            <TableRow>
              <TableCell colSpan={2} sx={{ pl: '24px !important' }}>
                合计
              </TableCell>
              <TableCell align="right">{total.orders}</TableCell>
              <TableCell align="right">{amount(total.quantity)}</TableCell>
              <TableCell align="right">
                {unitPrice(total.quantity ? total.amount / total.quantity : 0)}
              </TableCell>
              <TableCell align="right">{money(total.amount)}</TableCell>
              <TableCell />
              <TableCell sx={{ pr: '24px !important' }} />
            </TableRow>
          </TableFooter>
        )}
      </Table>
    </TableContainer>
  );
}
